import { LoadingOutlined } from '@ant-design/icons';
import { Avatar, Button, Spin, Upload } from 'antd';
import { RcFile } from 'antd/es/upload';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import React, { useState } from 'react';
import { useRecoilState } from 'recoil';

import { ShouldRender } from '../../shared/basic/ShouldRender';
import { formateErrorAppwriteObject } from '../../shared/formateErrorObject';
import { getUserInitials } from '../../shared/getInitials';
import { notify } from '../../shared/notify';
import PencilEdit from '../../../../assets/Icons/PencilEdit';
import ProfileIcons from '../../../../assets/Icons/ProfileIcons';
import { user } from '../../../atoms/user';
import { verifyModal } from '../../../atoms/verifyModal';
import appwriteService from '../../../config/appwrite';
import { uploadProfileImage } from '../../../services/user';
import { User } from '../../../utils/interfaces/User';

const HeroSection = () => {
  const [userData, setUserData] = useRecoilState<User | undefined>(user);
  const [, setVisibleVerifyEmailModal] = useRecoilState(verifyModal);
  const screens = useBreakpoint();

  const [avatar, setAvatar] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const handleImageUpload = async (file: RcFile) => {
    try {
      setLoading(true);
      const response = await uploadProfileImage(file);
      setUserData((prevUserData) => {
        if (!prevUserData) return undefined;
        return {
          ...prevUserData,
          avatar: response?.data?.avatar || '',
        };
      });
      setAvatar(URL.createObjectURL(file));
      notify('Profile image updated successfully', 'success');
    } catch (error) {
      notify('Failed to update profile image', 'error');
    } finally {
      setLoading(false);
    }
  };

  const beforeUpload = (file: RcFile) => {
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      notify('Image must be smaller than 2MB!', 'error');
    }
    return isLt2M;
  };

  const sendVerificationLink = async () => {
    try {
      await appwriteService.sendVerificationEmail();
      setVisibleVerifyEmailModal(true);
    } catch (error: any) {
      notify(formateErrorAppwriteObject(error));
    }
  };
  const avatarSrc =
    avatar ||
    (userData?.avatar &&
      `${process.env.NEXT_PUBLIC_IMAGE_BASE_URL_ECOM}/${userData?.avatar}`); // Use uploaded avatar first, fallback to userData.avatar

  return (
    <div className=''>
      <div className=' -translate-y-5'>
        <div className='w-full borderless-shadow rounded-2xl flex h-full flex-row-reverse justify-end gap-x-4 md:gap-x-8'>
          <div className='flex flex-col h-full '>
            <span className='font-bold text-white text-base md:text-xl'>
              Profile picture
            </span>
            <span className='text-[#646464] text-xs md:text-lg font-medium'>
              This picture will be visible to all
            </span>
            <ShouldRender check={!userData?.email_verified_at}>
              <Button
                size='small'
                type='text'
                onClick={() => {
                  sendVerificationLink();
                }}
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  background:
                    'linear-gradient(180deg, rgba(97, 153, 255, 0.1) 0%, rgba(4, 92, 251, 0.1) 100%)',
                  border: 'none',
                  borderRadius: '20px',
                  padding: '5px 20px',
                  zIndex: 1,
                }}
                className='h-[28px] md:h-[34px] hover:text-white text-xs md:text-sm w-fit md:px-4 mt-2 font-semibold'
              >
                <span
                  style={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: -1,
                    borderRadius: 'inherit',
                    padding: '1.3px',
                    background:
                      'linear-gradient(180deg, #6199FF 0%, #045CFB 100%)',
                    WebkitMask:
                      'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                  }}
                />
                Get Verified
              </Button>
            </ShouldRender>
            <ShouldRender check={userData?.email_verified_at}>
              <Button
                size='small'
                type='text'
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  background:
                    'linear-gradient(180deg, #6199FF 0%, #045CFB 100%)',

                  border: 'none',
                  borderRadius: '20px',
                  padding: '5px 20px',
                  zIndex: 1,
                }}
                className='h-[28px] md:h-[34px] hover:text-white text-xs md:text-sm w-fit md:px-4 mt-2 font-semibold'
              >
                <span
                  style={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: -1,
                    borderRadius: 'inherit',
                    padding: '1.3px',
                    background:
                      'linear-gradient(180deg, #6199FF 0%, #045CFB 100%)',
                    WebkitMask:
                      'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',

                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                  }}
                />
                Verified
              </Button>
            </ShouldRender>
          </div>

          <div
            className=''
            style={{ position: 'relative', display: 'inline-block' }}
          >
            <Spin
              spinning={loading}
              indicator={<LoadingOutlined style={{ fontSize: 20 }} spin />}
            >
              <ShouldRender check={userData?.email_verified_at}>
                <div
                  style={{
                    background:
                      'linear-gradient(180deg, #6199FF 0%, #045CFB 100%)',
                  }}
                  className='rounded-full p-0.5'
                >
                  <div className='rounded-full p-0.5 bg-bgColor'>
                    {' '}
                    <Avatar
                      size={!screens?.md ? 56 : 120}
                      alt='profile-picture'
                      style={{
                        fontSize: '24px',
                        background: !avatarSrc
                          ? 'linear-gradient(180deg, #FF5612 0%, #C72707 100%)'
                          : undefined,
                        border: '1px solid #FFFFFF0D',
                      }}
                      className={clsx(!avatarSrc ? 'bg-primary' : 'bg-bgColor')}
                      src={avatarSrc}
                    >
                      {(!avatarSrc || loading) && !userData?.name ? (
                        <ProfileIcons width='14' />
                      ) : (
                        userData?.name && getUserInitials(userData.name)
                      )}
                    </Avatar>
                  </div>
                </div>
              </ShouldRender>
              <ShouldRender check={!userData?.email_verified_at}>
                <Avatar
                  size={!screens?.md ? 60 : 120}
                  alt='profile-picture'
                  style={{
                    background: !avatarSrc
                      ? 'linear-gradient(180deg, #FF5612 0%, #C72707 100%)'
                      : undefined,
                    border: '1px solid #FFFFFF0D',
                  }}
                  className={clsx(!avatarSrc ? 'bg-primary' : 'bg-bgColor')}
                  src={avatarSrc}
                >
                  {(!avatarSrc || loading) && !userData?.name ? (
                    <ProfileIcons width='14' />
                  ) : (
                    userData?.name && getUserInitials(userData.name)
                  )}
                </Avatar>
              </ShouldRender>
            </Spin>
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                right: 0,

                borderRadius: '50%',
                cursor: 'pointer',
              }}
            >
              <Upload
                showUploadList={false}
                disabled={loading}
                beforeUpload={beforeUpload}
                customRequest={({ file }) => handleImageUpload(file as RcFile)}
              >
                <div className='bg-bgColor md:translate-y-1 rounded-full flex justify-center items-center'>
                  <PencilEdit width={!screens.md ? '28' : undefined} />
                </div>
              </Upload>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
